import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  MapPinIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useUserStore } from '../../../store/userStore';
import { useSeatStore } from '../../../store/seatStore';
import { seatApi, userApi } from '../../../services/api';
import { Seat } from '../../../types';
import CinemaStyleSeatSelection from './CinemaStyleSeatSelection';
import { Zone, SeatInZone } from '../../../services/zoneApi';

interface SeatSelectionProps {
  onSeatSelect?: (seat: Seat) => void;
}

const SeatSelection: React.FC<SeatSelectionProps> = ({ onSeatSelect }) => {
  const navigate = useNavigate();
  const { currentUser, setCurrentSession } = useUserStore();
  const [useNewSystem, setUseNewSystem] = useState(true); // 새로운 시스템 사용 여부

  // 새로운 시스템 완료 처리
  const handleNewSystemComplete = async (
    zone: Zone,
    seat: SeatInZone,
    action: 'checkin' | 'reserve'
  ) => {
    if (!currentUser) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      // VIP 좌석 체크
      const vipCheckResponse = await fetch(`/api/v1/vip-check/check/${seat.id}`);
      const vipData = await vipCheckResponse.json();
      
      if (vipData.is_vip && !vipData.can_reserve) {
        alert('이 좌석은 VIP 회원 전용 좌석입니다.');
        return;
      }

      if (action === 'checkin') {
        // 좌석 예약
        await userApi.reserveSeat(currentUser.id, {
          seat_number: seat.id,
        });

        // 체크인
        const checkInResponse = await userApi.checkIn(currentUser.id, {
          seat_number: seat.id,
        });

        // 세션 설정
        const session = {
          id: checkInResponse.session_id,
          user_id: currentUser.id,
          seat_id: checkInResponse.seat_number,
          check_in_at: checkInResponse.check_in_time,
          total_minutes: 0,
          study_minutes: 0,
          break_minutes: 0,
          status: 'active' as const,
          minutes_deducted: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        setCurrentSession(session);
        alert(`${zone.name} ${seat.seat_number}번 좌석에 입실되었습니다.`);
        navigate('/');
      } else if (action === 'reserve') {
        // 예약만 처리
        await userApi.reserveSeat(currentUser.id, {
          seat_number: seat.id,
        });
        alert(`${zone.name} ${seat.seat_number}번 좌석이 예약되었습니다.`);
        navigate('/');
      }
    } catch (error) {
      console.error('Seat action failed:', error);
      alert(
        `${action === 'checkin' ? '입실' : '예약'} 처리 중 오류가 발생했습니다.`
      );
    }
  };

  // 기존 시스템 hooks - 조건부 return 전에 호출해야 함
  const {
    seats,
    selectedSeat,
    filters,
    isLoading,
    setSeats,
    setSelectedSeat,
    setFilters,
    setLoading,
  } = useSeatStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isReserving, setIsReserving] = useState(false);

  // 디버깅: currentUser 상태 확인
  useEffect(() => {
  }, [currentUser]);

  // 좌석 데이터 로드
  useEffect(() => {
    const loadSeats = async () => {
      setLoading(true);
      try {
        const response = await seatApi.getAll(filters);
        setSeats(response.data);
      } catch (error) {
        console.error('Failed to load seats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSeats();
  }, [filters, setSeats, setLoading]);

  // 좌석 필터링 (안전한 처리)
  const filteredSeats = (seats || []).filter((seat) => {
    const matchesSearch =
      searchTerm === '' ||
      seat.seat_number.toString().includes(searchTerm) ||
      seat.location_description
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  // 좌석 상태에 따른 스타일
  const getSeatStyle = (seat: Seat) => {
    const baseStyle =
      'w-16 h-16 rounded-lg border-2 flex items-center justify-center text-base font-bold cursor-pointer transition-all duration-200';

    if (seat.is_out_of_order) {
      return `${baseStyle} bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed`;
    }

    if (!seat.is_available) {
      return `${baseStyle} bg-red-50 border-red-300 text-red-600 cursor-not-allowed`;
    }

    if (selectedSeat?.seat_number === seat.seat_number) {
      return `${baseStyle} bg-primary-500 border-primary-600 text-white shadow-lg`;
    }

    return `${baseStyle} bg-green-50 border-green-300 text-green-700 hover:bg-green-100 hover:border-green-400`;
  };

  // 좌석 선택 핸들러
  const handleSeatSelect = (seat: Seat) => {
    if (!seat.is_available || seat.is_out_of_order) return;

    const newSelectedSeat =
      selectedSeat?.seat_number === seat.seat_number ? null : seat;
    setSelectedSeat(newSelectedSeat);

    // 외부 콜백 호출 (예약 화면에서 사용)
    if (onSeatSelect && newSelectedSeat) {
      onSeatSelect(newSelectedSeat);
    }
  };

  // 체크인 처리
  const handleCheckIn = async () => {

    if (!selectedSeat) {
      alert('좌석을 선택해주세요.');
      return;
    }

    if (!currentUser) {
      alert('로그인이 필요합니다.');
      return;
    }

    setIsReserving(true);
    try {
      // 좌석 예약
      await userApi.reserveSeat(currentUser.id, {
        seat_number: selectedSeat.seat_number,
      });

      // 체크인
      const checkInResponse = await userApi.checkIn(currentUser.id, {
        seat_number: selectedSeat.seat_number,
      });

      // 임시로 StudySession 객체 생성 (실제로는 백엔드에서 받아와야 함)
      const session = {
        id: checkInResponse.session_id,
        user_id: currentUser.id,
        seat_id: checkInResponse.seat_number,
        check_in_at: checkInResponse.check_in_time,
        total_minutes: 0,
        study_minutes: 0,
        break_minutes: 0,
        status: 'active' as const,
        minutes_deducted: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setCurrentSession(session);
      alert(`${selectedSeat.seat_number}번 좌석에 입실되었습니다.`);
      navigate('/');
    } catch (error) {
      console.error('Check-in failed:', error);
      alert('입실 처리 중 오류가 발생했습니다.');
    } finally {
      setIsReserving(false);
    }
  };

  // 좌석을 그리드로 배치 (실제로는 좌석 배치도에 따라 달라짐)
  const renderSeatGrid = () => {
    // 안전한 처리: filteredSeats가 비어있거나 undefined인 경우 처리
    if (!filteredSeats || filteredSeats.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          좌석 정보를 불러오는 중입니다...
        </div>
      );
    }

    const maxSeatNumber = Math.max(
      ...filteredSeats.map((seat) => seat.seat_number)
    );
    const rows = Math.ceil(maxSeatNumber / 10);

    return Array.from({ length: rows }, (_, rowIndex) => (
      <div key={rowIndex} className="flex justify-center space-x-3 mb-3">
        {Array.from({ length: 10 }, (_, colIndex) => {
          const seatNumber = rowIndex * 10 + colIndex + 1;
          const seat = filteredSeats.find((s) => s.seat_number === seatNumber);

          if (!seat) {
            return <div key={colIndex} className="w-16 h-16"></div>;
          }

          return (
            <div
              key={seat.seat_number}
              className={getSeatStyle(seat)}
              onClick={() => handleSeatSelect(seat)}
              title={`${seat.seat_number}번 좌석 ${seat.location_description || ''}`}
            >
              {seat.seat_number}
            </div>
          );
        })}
      </div>
    ));
  };

  // 새로운 시스템 사용
  if (useNewSystem) {
    return (
      <div className="relative">

        <CinemaStyleSeatSelection
          onComplete={handleNewSystemComplete}
          onCancel={() => navigate('/')}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 lg:p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 lg:p-6">
      {/* 시스템 전환 토글 (개발용) */}
      <div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-lg p-2">
        <button
          onClick={() => setUseNewSystem(true)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          새로운 시스템으로 전환
        </button>
      </div>

      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          좌석 선택 (영화관 스타일)
        </h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/seats')}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
          >
            🗺️ 통합 레이아웃으로 돌아가기
          </button>
          <button
            onClick={() => navigate('/seat-reservation')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            💳 새로운 예약 시스템
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden p-2 text-gray-400 hover:text-gray-600"
          >
            <FunnelIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          {/* 검색 */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="좌석 번호 또는 위치 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* 필터 (데스크탑) */}
          <div className="hidden lg:flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.exclude_out_of_order}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    exclude_out_of_order: e.target.checked,
                  })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-600">
                점검중 좌석 숨기기
              </span>
            </label>
          </div>
        </div>

        {/* 모바일 필터 */}
        {showFilters && (
          <div className="lg:hidden mt-4 pt-4 border-t border-gray-200">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.exclude_out_of_order}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    exclude_out_of_order: e.target.checked,
                  })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-600">
                점검중 좌석 숨기기
              </span>
            </label>
          </div>
        )}
      </div>

      {/* 범례 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-3">좌석 상태</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-50 border-2 border-green-300 rounded mr-2"></div>
            <span className="text-gray-600">이용 가능</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-50 border-2 border-red-300 rounded mr-2"></div>
            <span className="text-gray-600">사용 중</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-gray-100 border-2 border-gray-300 rounded mr-2"></div>
            <span className="text-gray-600">점검 중</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-primary-500 border-2 border-primary-600 rounded mr-2"></div>
            <span className="text-gray-600">선택됨</span>
          </div>
        </div>
      </div>

      {/* 좌석 그리드 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">좌석 배치도</h3>
          <div className="text-sm text-gray-500">
            총 {filteredSeats ? filteredSeats.length : 0}개 좌석
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-max">{renderSeatGrid()}</div>
        </div>
      </div>

      {/* 선택된 좌석 정보 및 체크인 */}
      {selectedSeat && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <MapPinIcon className="h-6 w-6 text-primary-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedSeat.seat_number}번 좌석
              </h3>
              {selectedSeat.location_description && (
                <p className="text-gray-600 mt-1">
                  위치: {selectedSeat.location_description}
                </p>
              )}
              <div className="mt-4">
                <button
                  onClick={handleCheckIn}
                  disabled={isReserving}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isReserving ? (
                    <>
                      <ClockIcon className="h-5 w-5 mr-2 animate-spin" />
                      입실 처리 중...
                    </>
                  ) : (
                    '이 좌석으로 입실하기'
                  )}
                </button>
              </div>
            </div>
            <button
              onClick={() => setSelectedSeat(null)}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* 빈 상태 */}
      {!isLoading && (!filteredSeats || filteredSeats.length === 0) && (
        <div className="bg-white rounded-lg shadow p-6 text-center py-12">
          <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            조건에 맞는 좌석이 없습니다
          </h3>
          <p className="text-gray-500 mb-4">
            검색 조건을 변경하거나 필터를 해제해보세요.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setFilters({ exclude_out_of_order: true });
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            필터 초기화
          </button>
        </div>
      )}
    </div>
  );
};

export default SeatSelection;
